import {
  FieldValues,
  UseFormGetValues,
  UseFormSetValue,
} from "react-hook-form";
import { Dispatch, SetStateAction } from "react";
import {
  createMFD,
  CreateMFDProps,
  deleteMFD,
  DeleteMFDProps,
  getFinancialMovementDescList,
  MFDListProps,
  updateMFD,
  UpdateMFDProps,
} from "@/services/financial-movement-desc.service";
import { getFinancialMovementsDescFilter, MFDFRProps } from "./filter-obj";
import { toaster } from "@/components/ui/toaster";
import { AxiosError } from "axios";
import { NavigateFunction } from "react-router-dom";
import { AppRoutes } from "@/constants/routes";
import { handlePermissionError } from "@/utils/permission-check";
import { FinancialMovementDesc } from "./financial-movements-desc.schema";

interface ClearFiltersHandlerProps {
  setValue: UseFormSetValue<FieldValues>;
  setFiltersApplied: Dispatch<SetStateAction<boolean>>;
  submitFilters: (event: FieldValues) => Promise<void>;
  getValues: UseFormGetValues<FieldValues>;
}

export const clearFiltersHandler = ({
  setFiltersApplied,
  setValue,
  getValues,
  submitFilters,
}: ClearFiltersHandlerProps) => {
  setValue("name", "");
  setValue("code", "");

  submitFilters?.(getValues());
  setFiltersApplied(false);
};

interface SubmitFiltersHandlerProps {
  setOpenFilters: Dispatch<SetStateAction<boolean>>;
  setFiltersApplied: Dispatch<SetStateAction<boolean>>;
  setCurrentFilters: Dispatch<SetStateAction<{}>>;
  fetchFinancialMovementsDesc: (filters?: any) => Promise<void>;
  event: FieldValues;
}

export const submitFiltersHandler = async ({
  event,
  setCurrentFilters,
  setFiltersApplied,
  fetchFinancialMovementsDesc,
  setOpenFilters,
}: SubmitFiltersHandlerProps) => {
  setFiltersApplied(true);

  const filterObj: Partial<MFDListProps> = getFinancialMovementsDescFilter(
    event as MFDFRProps,
  );

  setCurrentFilters(filterObj);

  if (!Object.keys(filterObj)?.length) setFiltersApplied(false);

  await fetchFinancialMovementsDesc(filterObj);

  setOpenFilters(false);
};

export interface HandleDeleteFinancialMovementsDescriptionHandlerProps {
  setLoadingDeleteFinancialMovementsDescription: Dispatch<
    SetStateAction<boolean>
  >;
  setOpenConfirm: (value: React.SetStateAction<boolean>) => void;
  selection: string[];
  fetchFinancialMovementsDescription: (
    filters?: any,
    showLoad?: boolean,
  ) => Promise<void>;
  setSelection: Dispatch<React.SetStateAction<string[]>>;
}

export const handleDeleteFinancialMovementsDescriptionHandler = async ({
  setLoadingDeleteFinancialMovementsDescription,
  setOpenConfirm,
  fetchFinancialMovementsDescription,
  setSelection,
  selection,
}: HandleDeleteFinancialMovementsDescriptionHandlerProps) => {
  try {
    setLoadingDeleteFinancialMovementsDescription(true);

    const objToSend: DeleteMFDProps = {
      Codigos: selection.map(Number),
    };

    await deleteMFD(objToSend);

    toaster.create({
      type: "success",
      title: "Descripciones seleccionadas eliminadas con éxito.",
    });

    setOpenConfirm(false);
    setLoadingDeleteFinancialMovementsDescription(false);

    setSelection([]);

    await fetchFinancialMovementsDescription({}, false);
  } catch (error) {
    const errorObj = error as AxiosError<{ message: string }>;
    toaster.create({
      type: "error",
      title:
        errorObj?.response?.data?.message ??
        "No se pudieron eliminar las descripciones seleccionadas.",
    });
    throw error;
  }
};

export interface HandleCreateFinancialMovementsDescriptionProps {
  navigate: NavigateFunction;
  setLoadingCreateFinancialMovementsDesc: Dispatch<SetStateAction<boolean>>;
  watchedValues: FieldValues;
  fetchPermissions: () => Promise<void>;
}

export const handleCreateFinancialMovementsDescriptionHandler = async ({
  navigate,
  setLoadingCreateFinancialMovementsDesc,
  watchedValues,
  fetchPermissions,
}: HandleCreateFinancialMovementsDescriptionProps) => {
  try {
    setLoadingCreateFinancialMovementsDesc(true);

    const objToSend: CreateMFDProps = {
      nombre: watchedValues?.nombre,
      codigo: watchedValues?.codigo,
    };

    await createMFD(objToSend);

    toaster.create({
      type: "success",
      title: "Descripción de movimiento creada con éxito.",
    });

    navigate(AppRoutes.FINANCIAL_MOVEMENTS_DESC);
  } catch (error) {
    const errorObj = error as AxiosError<{ message: string; errors: string }>;
    const handled = await handlePermissionError(
      error,
      navigate,
      fetchPermissions,
      AppRoutes.FINANCIAL_MOVEMENTS_DESC,
    );
    if (!handled) {
      toaster.create({
        type: "error",
        title:
          errorObj?.response?.data?.errors ??
          "No se pudo crear la descripción de movimiento.",
      });
      throw error;
    }
  } finally {
    setLoadingCreateFinancialMovementsDesc(false);
  }
};

export interface HandleUpdateFinancialMovementsDescriptionProps {
  navigate: NavigateFunction;
  setLoadingUpdateFinancialMovementsDesc: Dispatch<SetStateAction<boolean>>;
  watchedValues: FieldValues;
  fetchPermissions: () => Promise<void>;
}

export const handleUpdateFinancialMovementsDescriptionHandler = async ({
  navigate,
  setLoadingUpdateFinancialMovementsDesc,
  watchedValues,
  fetchPermissions,
}: HandleUpdateFinancialMovementsDescriptionProps) => {
  try {
    setLoadingUpdateFinancialMovementsDesc(true);

    const objToSend: UpdateMFDProps = {
      nombre: watchedValues?.nombre,
      codigo: watchedValues?.codigo,
    };

    await updateMFD(objToSend);

    toaster.create({
      type: "success",
      title: "Descripción de movimiento actualizada con éxito.",
    });

    navigate(AppRoutes.FINANCIAL_MOVEMENTS_DESC);
  } catch (error) {
    const errorObj = error as AxiosError<{ message: string; errors: string }>;

    const handled = await handlePermissionError(
      error,
      navigate,
      fetchPermissions,
      AppRoutes.FINANCIAL_MOVEMENTS_DESC,
    );
    if (!handled) {
      toaster.create({
        type: "error",
        title:
          errorObj?.response?.data?.errors ??
          `No se pudo actualizar la descripción de movimiento`,
      });
      throw error;
    }

    throw error;
  } finally {
    setLoadingUpdateFinancialMovementsDesc(false);
  }
};

interface FetchFinancialMovementsDescHandlerProps {
  setLoadingFinancialMovementDesc: Dispatch<SetStateAction<boolean>>;
  setFinancialMovementsDesc: Dispatch<SetStateAction<FinancialMovementDesc[]>>;
  fiters?: any;
  navigate: NavigateFunction;
  showLoad?: boolean;
  fetchPermissions: () => Promise<void>;
}

export const fetchFinancialMovementsDescHandler = async ({
  setLoadingFinancialMovementDesc,
  setFinancialMovementsDesc,
  fiters,
  navigate,
  fetchPermissions,
  showLoad = true,
}: FetchFinancialMovementsDescHandlerProps) => {
  if (showLoad) setLoadingFinancialMovementDesc(true);
  try {
    const response = await getFinancialMovementDescList({ ...fiters });
    setFinancialMovementsDesc(response);
  } catch (error) {
    const handled = await handlePermissionError(
      error,
      navigate,
      fetchPermissions,
    );
    if (!handled) {
      toaster.create({
        type: "error",
        title: "No se pudieron obtener las descripciones de movimientos.",
      });
      throw error;
    }
  } finally {
    setLoadingFinancialMovementDesc(false);
  }
};

